import { useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CreateKitShipmentItemRequest,
  type CreateKitShipmentRequest,
  CreateKitShipmentRequestSchema,
  type LabKitDto,
} from '@ctms/contracts';
import {
  AlertTriangle,
  Calendar,
  Check,
  Package,
  Plus,
  Thermometer,
  Trash2,
  Truck,
  X,
} from 'lucide-react';

import { getApiErrorMessage } from '../../../api/client.js';
import { kitsApi } from '../kits.api.js';
import { kitQueryKeys } from '../kits.query-keys.js';

export interface ShipmentRegistrationDialogProps {
  trialId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ItemFormState {
  labKitId: number | '';
  batchNumber: string;
  expirationDate: string;
  quantity: number | '';
  comments: string;
}

const DEFAULT_ITEM: ItemFormState = {
  labKitId: '',
  batchNumber: '',
  expirationDate: '',
  quantity: 1,
  comments: '',
};

export function ShipmentRegistrationDialog({
  trialId,
  open,
  onOpenChange,
}: ShipmentRegistrationDialogProps): JSX.Element | null {
  const queryClient = useQueryClient();

  const [shipmentNumber, setShipmentNumber] = useState('');
  const [courierName, setCourierName] = useState('');
  const [receptionDate, setReceptionDate] = useState(new Date().toISOString().slice(0, 10));
  const [temperatureCondition, setTemperatureCondition] = useState('ambient');
  const [temperatureOk, setTemperatureOk] = useState(true);
  const [comments, setComments] = useState('');
  const [items, setItems] = useState<ItemFormState[]>([{ ...DEFAULT_ITEM }]);
  const [formError, setFormError] = useState<string | null>(null);

  // 1. Fetch laboratories of this trial
  const laboratoriesQuery = useQuery({
    queryKey: kitQueryKeys.laboratories.byTrial(trialId),
    queryFn: () => kitsApi.listLaboratories({ trialId, page: 1, limit: 100 }),
    enabled: open && trialId > 0,
    staleTime: 5 * 60 * 1000,
  });

  const laboratories = laboratoriesQuery.data?.items ?? [];
  const labIds = laboratories.map((l) => l.id);

  // 2. Fetch kits for all laboratories of this trial
  const kitsQuery = useQuery({
    queryKey: ['kits', 'by-trial-all-labs', trialId, labIds],
    queryFn: async (): Promise<LabKitDto[]> => {
      const allKits: LabKitDto[] = [];
      for (const lab of laboratories) {
        const page = await kitsApi.listKits({ trialLaboratoryId: lab.id, page: 1, limit: 100 });
        allKits.push(...page.items);
      }
      return allKits;
    },
    enabled: open && laboratories.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const availableKits = kitsQuery.data ?? [];

  const createMutation = useMutation({
    mutationFn: async (payload: CreateKitShipmentRequest) => kitsApi.createShipment(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kitQueryKeys.shipments.all });
      queryClient.invalidateQueries({ queryKey: kitQueryKeys.stock.all });
      queryClient.invalidateQueries({ queryKey: kitQueryKeys.kits.all });
      resetForm();
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      void getApiErrorMessage(err, 'Nie udało się zarejestrować przesyłki').then(setFormError);
    },
  });

  function resetForm(): void {
    setShipmentNumber('');
    setCourierName('');
    setReceptionDate(new Date().toISOString().slice(0, 10));
    setTemperatureCondition('ambient');
    setTemperatureOk(true);
    setComments('');
    setItems([{ ...DEFAULT_ITEM }]);
    setFormError(null);
  }

  function handleAddItem(): void {
    setItems((prev) => [...prev, { ...DEFAULT_ITEM }]);
  }

  function handleRemoveItem(index: number): void {
    if (items.length <= 1) return;
    setItems((prev) => prev.filter((_, i) => i !== index));
  }

  function handleItemChange<K extends keyof ItemFormState>(
    index: number,
    field: K,
    value: ItemFormState[K],
  ): void {
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setFormError(null);

    // Prepare payload for Zod validation
    const payloadItems: CreateKitShipmentItemRequest[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item || item.labKitId === '') {
        setFormError(`Wybierz rodzaj zestawu dla pozycji #${i + 1}`);
        return;
      }
      const kit = availableKits.find((k) => k.id === item.labKitId);
      if (kit?.expiration && !item.expirationDate.trim()) {
        setFormError(
          `Data ważności jest wymagana dla zestawu "${kit.name}" w pozycji #${i + 1}`,
        );
        return;
      }
      const quantityNum = Number(item.quantity);
      if (Number.isNaN(quantityNum) || quantityNum <= 0) {
        setFormError(`Ilość w pozycji #${i + 1} musi być większa od zera`);
        return;
      }

      payloadItems.push({
        labKitId: item.labKitId,
        batchNumber: item.batchNumber.trim() || undefined,
        expirationDate: kit?.expiration ? item.expirationDate.trim() : null,
        quantity: quantityNum,
        comments: item.comments.trim() || undefined,
      });
    }

    const unvalidated = {
      trialId,
      shipmentNumber: shipmentNumber.trim(),
      courierName: courierName.trim() || undefined,
      receptionDate,
      temperatureCondition: temperatureCondition.trim() || undefined,
      temperatureOk,
      comments: comments.trim() || undefined,
      items: payloadItems,
    };

    const parsed = CreateKitShipmentRequestSchema.safeParse(unvalidated);
    if (!parsed.success) {
      const firstIssue = parsed.error.issues[0];
      setFormError(firstIssue ? firstIssue.message : 'Nieprawidłowe dane formularza');
      return;
    }

    createMutation.mutate(parsed.data);
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="shipment-registration-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-xs"
    >
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-xl border border-slate-700 bg-slate-900 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-950 text-cyan-400 border border-cyan-800/60">
              <Truck className="h-5 w-5" />
            </div>
            <div>
              <h2
                id="shipment-registration-title"
                className="text-lg font-semibold text-slate-100"
              >
                Rejestracja przesyłki zestawów laboratoryjnych
              </h2>
              <p className="text-xs text-slate-400">
                Wprowadź dane paczki od kuriera i pozycje materiałowe (REQ-KIT-007, REQ-KIT-008)
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
            aria-label="Zamknij"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form Body */}
        <form
          id="shipment-registration-form"
          noValidate
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto px-6 py-5 space-y-6"
        >
          {formError && (
            <div
              role="alert"
              className="flex items-start gap-3 rounded-lg border border-rose-900/60 bg-rose-950/40 p-3 text-sm text-rose-300"
            >
              <AlertTriangle className="h-5 w-5 shrink-0 text-rose-400" />
              <span>{formError}</span>
            </div>
          )}

          {/* Section 1: Shipment Header Data */}
          <div className="space-y-4 rounded-lg border border-slate-800 bg-slate-950/50 p-4">
            <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
              <Package className="h-4 w-4 text-cyan-400" />
              <span>Dane przesyłki kurierskiej</span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label
                  htmlFor="shipment-number"
                  className="block text-xs font-medium text-slate-300 mb-1"
                >
                  Numer listu / przesyłki *
                </label>
                <input
                  id="shipment-number"
                  name="shipmentNumber"
                  type="text"
                  required
                  placeholder="np. DHL-84920412"
                  value={shipmentNumber}
                  onChange={(e) => setShipmentNumber(e.target.value)}
                  className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-hidden"
                />
              </div>

              <div>
                <label
                  htmlFor="courier-name"
                  className="block text-xs font-medium text-slate-300 mb-1"
                >
                  Firma kurierska / Dostawca
                </label>
                <input
                  id="courier-name"
                  name="courierName"
                  type="text"
                  placeholder="np. World Courier, DHL, Marken"
                  value={courierName}
                  onChange={(e) => setCourierName(e.target.value)}
                  className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-hidden"
                />
              </div>

              <div>
                <label
                  htmlFor="reception-date"
                  className="block text-xs font-medium text-slate-300 mb-1"
                >
                  Data dostawy do ośrodka *
                </label>
                <div className="relative">
                  <input
                    id="reception-date"
                    name="receptionDate"
                    type="date"
                    required
                    value={receptionDate}
                    onChange={(e) => setReceptionDate(e.target.value)}
                    className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:border-cyan-500 focus:outline-hidden"
                  />
                  <Calendar className="pointer-events-none absolute right-3 top-2.5 h-4 w-4 text-slate-400" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
              <div>
                <label
                  htmlFor="temperature-condition"
                  className="block text-xs font-medium text-slate-300 mb-1"
                >
                  Warunki transportu (temperatura)
                </label>
                <div className="relative">
                  <select
                    id="temperature-condition"
                    name="temperatureCondition"
                    value={temperatureCondition}
                    onChange={(e) => setTemperatureCondition(e.target.value)}
                    className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:border-cyan-500 focus:outline-hidden"
                  >
                    <option value="ambient">Temperatura pokojowa (15–25°C)</option>
                    <option value="cold_2_8">Chłodniczy (2–8°C)</option>
                    <option value="frozen_minus_20">Zamrożony (-20°C)</option>
                    <option value="frozen_minus_80">Głębokie mrożenie (-80°C / suchy lód)</option>
                    <option value="other">Inne / specyficzne</option>
                  </select>
                  <Thermometer className="pointer-events-none absolute right-3 top-2.5 h-4 w-4 text-slate-400" />
                </div>
              </div>

              <div className="flex items-center pt-5">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    id="temperature-ok"
                    name="temperatureOk"
                    type="checkbox"
                    checked={temperatureOk}
                    onChange={(e) => setTemperatureOk(e.target.checked)}
                    className="h-4 w-4 rounded-sm border-slate-700 bg-slate-800 text-cyan-600 focus:ring-cyan-500"
                  />
                  <span className="text-xs text-slate-300">
                    Warunki transportu zgodne (brak przekroczeń)
                  </span>
                </label>
              </div>

              <div>
                <label
                  htmlFor="shipment-comments"
                  className="block text-xs font-medium text-slate-300 mb-1"
                >
                  Komentarz do przesyłki
                </label>
                <input
                  id="shipment-comments"
                  name="comments"
                  type="text"
                  placeholder="np. Stan opakowania nienaruszony"
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-hidden"
                />
              </div>
            </div>
          </div>

          {/* Section 2: Items List */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
                <span>Zawartość przesyłki (zestawy i ilości)</span>
                <span className="rounded-full bg-slate-800 px-2 py-0.5 text-xs text-slate-400 font-mono">
                  {items.length}
                </span>
              </h3>
              <button
                type="button"
                id="add-shipment-item-button"
                onClick={handleAddItem}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs font-medium text-cyan-300 hover:bg-slate-700 hover:text-cyan-200"
              >
                <Plus className="h-3.5 w-3.5" />
                <span>Dodaj kolejny zestaw</span>
              </button>
            </div>

            {items.map((item, index) => {
              const selectedKit = availableKits.find((k) => k.id === item.labKitId);
              const isExpirationRequired = selectedKit?.expiration ?? true;

              return (
                <div
                  key={index}
                  data-testid={`shipment-item-row-${index}`}
                  className="relative rounded-lg border border-slate-800 bg-slate-950/40 p-4 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-cyan-400">
                      Pozycja #{index + 1}
                    </span>
                    {items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="text-slate-400 hover:text-rose-400"
                        title="Usuń pozycję"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="md:col-span-2">
                      <label
                        htmlFor={`item-kit-${index}`}
                        className="block text-xs font-medium text-slate-300 mb-1"
                      >
                        Typ zestawu laboratoryjnego *
                      </label>
                      <select
                        id={`item-kit-${index}`}
                        name={`itemKit-${index}`}
                        required
                        value={item.labKitId}
                        onChange={(e) =>
                          handleItemChange(
                            index,
                            'labKitId',
                            e.target.value === '' ? '' : Number(e.target.value),
                          )
                        }
                        className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:border-cyan-500 focus:outline-hidden"
                      >
                        <option value="">-- Wybierz zestaw z katalogu --</option>
                        {availableKits.map((k) => (
                          <option key={k.id} value={k.id}>
                            {k.name} ({k.expiration ? 'terminowy' : 'bezterminowy'})
                            {k.localisation ? ` [${k.localisation}]` : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label
                        htmlFor={`item-batch-${index}`}
                        className="block text-xs font-medium text-slate-300 mb-1"
                      >
                        Numer serii / partii
                      </label>
                      <input
                        id={`item-batch-${index}`}
                        name={`itemBatch-${index}`}
                        type="text"
                        placeholder="np. LOT-2026-B"
                        value={item.batchNumber}
                        onChange={(e) => handleItemChange(index, 'batchNumber', e.target.value)}
                        className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-hidden"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor={`item-quantity-${index}`}
                        className="block text-xs font-medium text-slate-300 mb-1"
                      >
                        Liczba sztuk *
                      </label>
                      <input
                        id={`item-quantity-${index}`}
                        name={`itemQuantity-${index}`}
                        type="number"
                        min="1"
                        step="1"
                        required
                        value={item.quantity}
                        onChange={(e) =>
                          handleItemChange(
                            index,
                            'quantity',
                            e.target.value === '' ? '' : Number(e.target.value),
                          )
                        }
                        className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:border-cyan-500 focus:outline-hidden font-mono"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-1">
                    <div className="md:col-span-2">
                      <label
                        htmlFor={`item-exp-date-${index}`}
                        className="block text-xs font-medium text-slate-300 mb-1"
                      >
                        Data ważności {isExpirationRequired ? '*' : '(brak dla zestawu bezterminowego)'}
                      </label>
                      <input
                        id={`item-exp-date-${index}`}
                        name={`itemExpDate-${index}`}
                        type={isExpirationRequired ? 'date' : 'text'}
                        disabled={!isExpirationRequired}
                        required={isExpirationRequired}
                        placeholder={isExpirationRequired ? 'RRRR-MM-DD' : 'Bezterminowy'}
                        value={isExpirationRequired ? item.expirationDate : ''}
                        onChange={(e) =>
                          handleItemChange(index, 'expirationDate', e.target.value)
                        }
                        className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-hidden disabled:opacity-50 disabled:bg-slate-900"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label
                        htmlFor={`item-comment-${index}`}
                        className="block text-xs font-medium text-slate-300 mb-1"
                      >
                        Uwagi do pozycji
                      </label>
                      <input
                        id={`item-comment-${index}`}
                        name={`itemComment-${index}`}
                        type="text"
                        placeholder="np. Dodatkowe probówki w kartonie"
                        value={item.comments}
                        onChange={(e) => handleItemChange(index, 'comments', e.target.value)}
                        className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-hidden"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-slate-800 px-6 py-4 bg-slate-950/60 rounded-b-xl">
          <p className="text-xs text-slate-400">
            Przyjęte pozycje natychmiast zasilą stan magazynowy ośrodka (REQ-KIT-008).
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-md border border-slate-700 px-4 py-2 text-xs font-medium text-slate-300 hover:bg-slate-800"
            >
              Anuluj
            </button>
            <button
              type="submit"
              form="shipment-registration-form"
              id="submit-shipment-button"
              disabled={createMutation.isPending}
              className="inline-flex items-center gap-2 rounded-md bg-cyan-600 px-4 py-2 text-xs font-semibold text-white shadow-xs hover:bg-cyan-500 disabled:opacity-50"
            >
              <Check className="h-4 w-4" />
              <span>{createMutation.isPending ? 'Zapisywanie...' : 'Zarejestruj dostawę'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
